import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { DataChangeEmitPayload, DataChangeEmitWhat, DataChangeEmitWho } from '../models/data-change-emit/data-change-emit-payload';
import { Attachment } from '../entities/Attachment';
import { DataService } from './data.service';
import { Callback, CallbackNames } from '../models/db/db-transaction-callback';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RecordNotFoundException, RecordSoftDeletedException } from '../utils/exceptions';
import { InFilter } from '../baseServices/filter.interface';
import { Check, EntityHelper } from '../utils/entity-helper';

@Injectable({
  providedIn: 'root',
})
export class AttachmentService extends DataService {
  private emitChangeSource = new Subject<DataChangeEmitPayload<Attachment>>();
  changeEmitted$ = this.emitChangeSource.asObservable();

  constructor() {
    super();
  }

  // #region Utility

  emitChange(attachment: Attachment, who: DataChangeEmitWho, what: DataChangeEmitWhat) {
    this.emitChangeSource.next({ data: attachment, who: who, what: what });
  }

  getEmitChangeCallback(attachment: Attachment, who: DataChangeEmitWho, what: DataChangeEmitWhat): Callback {
    return {
      name: CallbackNames.EmitChangeForAttachment + '_' + attachment.RecordId,
      fn: this.emitChange.bind(this, attachment, who, what),
    };
  }

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.Attachment], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getAttachmentByOnlineId(onlineId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Attachment> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByOnlineId<Attachment>(StoreName.Attachment, onlineId, Attachment, transaction);

    if (result === null) {
      throw new RecordNotFoundException(onlineId);
    }

    const record = result as Attachment;

    if (this.checkIsAttachmentSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(onlineId);
    }

    return record;
  }

  async getAttachmentByRecordId(recordId: number, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Attachment> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<Attachment>(StoreName.Attachment, recordId, Attachment, transaction);

    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as Attachment;

    if (this.checkIsAttachmentSoftDeleted(record) && !includeDeleted) {
      throw new RecordSoftDeletedException(recordId);
    }

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllAttachments(filter: any = undefined, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Attachment[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<Attachment>(StoreName.Attachment, filter, Attachment, transaction);

    let records = result as Attachment[];

    if (!includeDeleted) {
      records = records.filter(attachment => !this.checkIsAttachmentSoftDeleted(attachment));
    }

    return records;
  }

  async getAttachmentsByOnlineIds(onlineIds: number[], existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<Attachment[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const filter: InFilter = { in: { field: 'Id', searchArray: onlineIds } };

    const result = await this.repository.getAll<Attachment>(StoreName.Attachment, filter, Attachment, transaction);

    let records = result as Attachment[];

    if (!includeDeleted) {
      records = result.filter(attachment => !this.checkIsAttachmentSoftDeleted(attachment));
    }

    return records;
  }

  // #endregion
  // region Low-level Create

  private async createAttachment(who: DataChangeEmitWho, recordToCreate: Attachment, existingTransaction?: DbTransaction, includeDeleted: boolean = false): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LC1. Validate action
    await this.validateAttachmentCreation(transaction);

    // TODO: Impelment LC2 and LC3

    // LC4. Repository call
    const recordId = await this.repository.create(recordToCreate, StoreName.Attachment, transaction);

    // LC5. Get created record
    const createdRecord = await this.getAttachmentByRecordId(recordId, transaction, includeDeleted);

    // LC6. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(createdRecord, who, DataChangeEmitWhat.Create));

    // LC7. Return id
    return recordId;
  }

  // #endregion
  // region Low-level Update

  async updateAttachment(who: DataChangeEmitWho, recordToUpdate: Attachment, skipLocalCopyValidation: boolean, localCopy: Attachment | null, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // LU1. Get existing record
    const existingRecord = await this.getAttachmentByRecordId(recordToUpdate.RecordId, transaction);

    // LU2. Validate action
    await this.validateAttachmentUpdate(recordToUpdate, transaction);

    // LU3. Idempotent check
    if (EntityHelper.checkAllFieldsEqual(existingRecord, recordToUpdate)) {
      return;
    }

    // LU4. Validate local copy
    if (!skipLocalCopyValidation) {
      const checks: Check<Attachment>[] = [{ name: 'ImmutableFields', function: this.checkImmutableFieldsMatch }];
      EntityHelper.validateLocalCopy<Attachment>(localCopy, existingRecord, checks);
    }

    // LU5. Validate model
    await this.validateAttachment(recordToUpdate, transaction);

    // LU6. Repository call
    await this.repository.update(recordToUpdate, StoreName.Attachment, transaction);

    // LU7. Get updated record
    const updatedRecord = await this.getAttachmentByRecordId(recordToUpdate.RecordId, transaction);

    // LU8. Report changes
    transaction.addOnCommitCallback(this.getEmitChangeCallback(updatedRecord, who, DataChangeEmitWhat.Update));

    // LU9. Return
    return;
  }

  // #endregion
  // region Low-level Delete

  // #endregion
  // region Low-level Validate Model

  async validateAttachment(attachment: Attachment, existingTransaction?: DbTransaction): Promise<void> {
    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Low-level Validate Action

  async validateAttachmentCreation(existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  async validateAttachmentUpdate(attachment: Attachment, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // TODO: Implement validation logic

    return;
  }

  // #endregion
  // region Mid-level Get

  // #endregion
  // region Mid-level Create

  // #endregion
  // region Mid-level Update

  // #endregion
  // region Mid-level Delete

  // #endregion
  // region Mid-level Validate

  // #endregion
  // region Mid-level Upsert

  // #endregion
  // region Mid-level Sync

  async upsertAttachmentFromSync(who: DataChangeEmitWho, recordToUpsert: Attachment, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    if (who !== DataChangeEmitWho.Sync) {
      throw new Error('Must originate from Sync.');
    }

    try {
      const existingRecord = await this.getAttachmentByOnlineId(recordToUpsert.Id, transaction, true);
      recordToUpsert.RecordId = existingRecord.RecordId;
      await this.updateAttachment(who, recordToUpsert, true, null, transaction);
    } catch (e) {
      if (e instanceof RecordNotFoundException) {
        await this.createAttachment(who, recordToUpsert, transaction, true);
      }
    }

    return;
  }

  // #endregion
  // region Check

  private checkIsAttachmentSoftDeleted(record: Attachment): boolean {
    return record.IsDeleted;
  }

  private checkImmutableFieldsMatch(a: Attachment, b: Attachment): boolean {
    return a.UniqueId === b.UniqueId && a.FileName === b.FileName;
  }

  // #endregion
  // region Lookup

  // #endregion
  // region Transform

  // #endregion
  // region Other

  // #endregion
}
